/* eslint-disable @typescript-eslint/no-unused-vars */

import ButtonCst from "@components/ButtonCst";
import { SessionGetInterface } from "@components/EditionFormCst/types";
import ModalCst from "@components/ModalCst";
import { ExpandMore, Delete, Edit } from "@mui/icons-material";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { isTrueValue } from "@utils/utilsValidators";
import Alert from "@mui/material/Alert";
import { Grid } from "@mui/material";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import SessionInvitationModalCst from "@components/ModalCst/SessionInvitationModalCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";

// Interface
interface SessionAccordionCstInterface {
  data: SessionGetInterface;
  index: number;
  onEdit?: (data: any, index: number) => void;
  onDelete?: (data: any) => void;
  isVirtual?: boolean;
  isDetail?: boolean;
  disabled?: boolean;
}

// eslint-disable-next-line no-empty-pattern
const SessionAccordionCst: React.FC<SessionAccordionCstInterface> = ({
  data,
  index,
  onEdit,
  onDelete,
  isVirtual,
  isDetail,
  disabled,
}) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { t } = useTranslation(["form"]);
  const { t: inputTranslations } = useTranslation(["input"]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [QRType, setQRType] = useState("");
  const [idMeeting, setIdMeeting] = useState<number | string | null>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [roleView, setRoleView] = useState<boolean>(true);
  const [typeMeeting, setTypeMeeting] = useState<string | null>(null);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const [confirmResendNotifications, setConfirmResendNotifications] =
    useState(false);
  const [resendStatus, setResendStatus] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const isAddPage = pathname.includes("add");
  const location = useLocation();
  const editionId = location.pathname.split("/")[6];
  const corporateId = location.pathname.split("/")[2];

  const { trigger: triggerResendNotifications } = useSWRMutation(
    editionId && data.id
      ? `${ServicesURL.resendSessionNotifications
          .replace("{editionId}", String(editionId))
          .replace("{sessionId}", String(data.id))}?corporateId=${corporateId}`
      : null,
    async (url: string) => {
      return fetcher<any>(url, MethodType.POST);
    },
    { onError: () => {} },
  );

  const handleResendClick = () => {
    setConfirmResendNotifications(true);
  };

  const executeResendNotifications = async () => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    try {
      const response = await triggerResendNotifications();
      if (response?.success) {
        setOpenModal(false);
        setOpenToast({
          s: StatusEnum.OK,
          message: t("resend-notifications-success", { ns: "modal" }),
        });
      } else {
        setOpenToast({
          s: StatusEnum.KO,
          message: t("resend-notifications-error", { ns: "modal" }),
        });
      }
    } catch (error) {
      console.error(error);
      setOpenToast({
        s: StatusEnum.KO,
        message: t("resend-notifications-error", { ns: "modal" }),
      });
    }
  };

  const stepConfig = [
    {
      step: 1,
      label: t("session-form.session-create"),
    },
    {
      step: 2,
      label: t("session-form.meeting-sent"),
      showParticipant: true,
    },
  ];

  const getActualStep = (data: SessionGetInterface) => {
    if (data.session_confirmed && data.online_meeting_updated_at) return 2;
    if (data.online_meeting_updated_at) return 1;

    return -10;
  };

  const [actualStep] = useState(() => getActualStep(data));

  const getActualStepOtherPhysical = (data: SessionGetInterface) => {
    if (!isVirtual || (isVirtual && ["OTHER"].includes(data.tool ?? ""))) {
      if (data.session_confirmed) {
        return 2;
      }
      return 1;
    }
    return -10;
  };

  const [actualStepOtherPhysical] = useState(() =>
    getActualStepOtherPhysical(data),
  );

  const getArrayString = (array: any) => {
    if (!!array && !!array.length) {
      const newArray = array.map((item: any) => {
        return (
          item.label ||
          (item.name && item.lastname
            ? item.name + " " + item.lastname
            : item.name || item.lastname || "-")
        );
      });
      return newArray.join();
    } else {
      return "-";
    }
  };

  return (
    <>
      <Accordion
        sx={{
          mt: isDetail ? "0" : "1.5rem",
          border: `1px solid ${theme.palette.text.tertiary}`,
          borderRadius: "4px",
          "&.Mui-expanded": {
            mt: isDetail ? "0" : "1.5rem",
          },
          "&.Mui-disabled": {
            background: "transparent",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            minHeight: "3rem",
            pl: "1.875rem",
            "&.Mui-expanded": {
              minHeight: "3rem",
            },
            "& .MuiAccordionSummary-content": {
              margin: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
            },
            "&.Mui-disabled": {
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                maxWidth: "45%",
                mt: { xs: 2, sm: 0 },
              }}
            >
              {t("session-form.session-label")} {index + 1}
            </Typography>

            {isVirtual && ["MSTEAMS", "GMEET"].includes(data.tool ?? "") && (
              <Box
                sx={{
                  maxWidth: { xs: "100%", sm: "55%" },
                  flexGrow: 1,
                  p: 2,
                  width: "100%",
                }}
              >
                <Stepper activeStep={actualStep}>
                  {stepConfig.map((step, i) => (
                    <Step key={step.label}>
                      <StepLabel
                        sx={{
                          "& .MuiStepIcon-root": {
                            color:
                              i <= actualStep - 1
                                ? theme.palette.primary.main
                                : theme.palette.action.disabled,
                          },
                          "& .MuiStepLabel-label": {
                            fontWeight: "normal",
                            color:
                              i <= actualStep - 1
                                ? theme.palette.text.primary
                                : theme.palette.action.disabled,
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="body2">{step.label}</Typography>
                          {step.showParticipant && (
                            <IconButton
                              aria-label={"Visualizza inviti"}
                              onClick={(event) => {
                                event.stopPropagation();
                                setOpenModal(!openModal);
                                setIdMeeting(data.online_meeting_id || null);
                                setTypeMeeting(data.tool || null);
                              }}
                              edge="end"
                              disabled={step.step !== actualStep}
                            >
                              <RecentActorsIcon />
                            </IconButton>
                          )}
                        </Box>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}

            {(!isVirtual ||
              (isVirtual && ["OTHER"].includes(data.tool ?? ""))) &&
              data.id != null && (
                <Box
                  sx={{
                    maxWidth: { xs: "100%", sm: "55%" },
                    flexGrow: 1,
                    p: 2,
                    width: "100%",
                  }}
                >
                  <Stepper activeStep={actualStepOtherPhysical}>
                    {stepConfig.map((step, i) => (
                      <Step key={step.label}>
                        <StepLabel
                          sx={{
                            "& .MuiStepIcon-root": {
                              color:
                                i <= actualStepOtherPhysical - 1
                                  ? theme.palette.primary.main
                                  : theme.palette.action.disabled,
                            },
                            "& .MuiStepLabel-label": {
                              fontWeight: "normal",
                              color:
                                i <= actualStepOtherPhysical - 1
                                  ? theme.palette.text.primary
                                  : theme.palette.action.disabled,
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="body2">
                              {step.label}
                            </Typography>
                            {step.showParticipant && (
                              <IconButton
                                aria-label={"Visualizza inviti"}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenModal(!openModal);
                                  setIdMeeting(data.id || null);
                                  setTypeMeeting(data.tool || null);
                                  setRoleView(false);
                                }}
                                edge="end"
                                disabled={step.step !== actualStepOtherPhysical}
                              >
                                <RecentActorsIcon />
                              </IconButton>
                            )}
                          </Box>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              rowGap: "1rem",
              [theme.breakpoints.down("sm")]: {
                gridTemplateColumns: "1fr",
              },
            }}
          >
            {isVirtual &&
              !isDetail &&
              ["MSTEAMS", "GMEET"].includes(data?.tool ?? "") &&
              data?.online_meeting_updated_at && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    gridColumn: "1 / -1",
                    p: 0,
                  }}
                >
                  <Grid item xs={12}>
                    <Alert
                      variant="outlined"
                      severity="info"
                      sx={{
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.text.tertiary,
                        "& .MuiAlert-icon": {
                          color: theme.palette.text.primary,
                        },
                      }}
                    >
                      {t("session-form.meeting-sent-alert").replace(
                        "%date",
                        typeof data?.online_meeting_updated_at === "string" ||
                          typeof data?.online_meeting_updated_at === "number"
                          ? dayjs(data?.online_meeting_updated_at)
                              .utc()
                              .format("DD-MM-YYYY h:mm A")
                          : "-",
                      )}
                    </Alert>
                  </Grid>
                </Box>
              )}
            <Box
              sx={{
                borderBottom: "1px solid white",
                p: "1rem",
              }}
            >
              <Typography fontWeight={600} mb={1}>
                {t("session-form.start-date")}
              </Typography>
              <Typography>
                {dayjs(data.start_date).format("DD-MM-YYYY h:mm A")}
              </Typography>
            </Box>
            <Box
              sx={{
                borderBottom: "1px solid white",
                p: "1rem",
              }}
            >
              <Typography fontWeight={600} mb={1}>
                {t("session-form.end-date")}
              </Typography>
              <Typography>
                {dayjs(data.end_date).format("DD-MM-YYYY h:mm A")}
              </Typography>
            </Box>
            {!isVirtual && (
              <>
                <Box
                  sx={{
                    borderBottom: "1px solid white",
                    p: "1rem",
                  }}
                >
                  <Typography fontWeight={600} mb={1}>
                    {t("session-form.site")}
                  </Typography>
                  <Typography>
                    {data.site?.label
                      ? data.site?.label
                      : getArrayString(data.site)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    borderBottom: "1px solid white",
                    p: "1rem",
                  }}
                >
                  <Typography fontWeight={600} mb={1}>
                    {t("session-form.rooms")}
                  </Typography>
                  <Typography>{getArrayString(data.rooms)}</Typography>
                </Box>
              </>
            )}
            <Box
              sx={{
                borderBottom: "1px solid white",
                p: "1rem",
              }}
            >
              <Typography fontWeight={600} mb={1}>
                {t("session-form.faculties")}
              </Typography>
              <Typography>{getArrayString(data.faculties)}</Typography>
            </Box>
            <Box
              sx={{
                borderBottom: "1px solid white",
                p: "1rem",
              }}
            >
              <Typography fontWeight={600} mb={1}>
                {t("session-form.tutor")}
              </Typography>
              <Typography>{getArrayString(data.tutors)}</Typography>
            </Box>
            {isVirtual && (
              <>
                <Box
                  sx={{
                    borderBottom: "1px solid white",
                    p: "1rem",
                  }}
                >
                  <Typography fontWeight={600} mb={1}>
                    {t("session-form.tool")}
                  </Typography>
                  <Typography>
                    {data.tool
                      ? inputTranslations(
                          `options.meeting_tool.${data.tool.toLowerCase()}`,
                        )
                      : "-"}
                  </Typography>
                </Box>
                {data.tool && data.tool == "MSTEAMS" && (
                  <>
                    <Box
                      mt={1}
                      sx={{
                        border: `1px solid ${theme.palette.text.tertiary}`,
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 1,
                        position: "relative",
                        gridColumn: "1 / -1",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          position: "absolute",
                          top: 0,
                          left: 10,
                          backgroundColor: theme.palette.background.paper,
                          transform: "translateY(-50%)",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("session-form.meeting-roles")}
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          rowGap: "1rem",
                          [theme.breakpoints.down("sm")]: {
                            gridTemplateColumns: "1fr",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.producers")}
                          </Typography>
                          <Typography>
                            {getArrayString(data.producer)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.presenters")}
                          </Typography>
                          <Typography>
                            {getArrayString(data.presenter)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.coorganizers")}
                          </Typography>
                          <Typography>
                            {getArrayString(data.coorganizer)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      mt={1}
                      sx={{
                        border: `1px solid ${theme.palette.text.tertiary}`,
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 1,
                        position: "relative",
                        gridColumn: "1 / -1",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          position: "absolute",
                          top: 0,
                          left: 10,
                          backgroundColor: theme.palette.background.paper,
                          transform: "translateY(-50%)",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("session-form.meeting-lobby")}
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          rowGap: "1rem",
                          [theme.breakpoints.down("sm")]: {
                            gridTemplateColumns: "1fr",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.is_dial_in_bypass_enabled")}
                          </Typography>
                          <Typography>
                            {data.is_dial_in_bypass_enabled ? "Si" : "No"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.scope")}
                          </Typography>
                          <Typography>
                            {data.scope
                              ? inputTranslations(
                                  `options.meeting_scope.${data.scope.toLowerCase()}`,
                                )
                              : "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      mt={1}
                      sx={{
                        border: `1px solid ${theme.palette.text.tertiary}`,
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 1,
                        position: "relative",
                        gridColumn: "1 / -1",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          position: "absolute",
                          top: 0,
                          left: 10,
                          backgroundColor: theme.palette.background.paper,
                          transform: "translateY(-50%)",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("session-form.meeting-general")}
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          rowGap: "1rem",
                          [theme.breakpoints.down("sm")]: {
                            gridTemplateColumns: "1fr",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.allow_meeting_chat")}
                          </Typography>
                          <Typography>
                            {data.allow_meeting_chat
                              ? inputTranslations(
                                  `options.meeting_chat.${data.allow_meeting_chat.toLowerCase()}`,
                                )
                              : "-"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.allow_attendee_to_enable_camera")}
                          </Typography>
                          <Typography>
                            {data.allow_attendee_to_enable_camera ? "Si" : "No"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.allow_attendee_to_enable_mic")}
                          </Typography>
                          <Typography>
                            {data.allow_attendee_to_enable_mic ? "Si" : "No"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.allow_breakout_rooms")}
                          </Typography>
                          <Typography>
                            {data.allow_breakout_rooms ? "Si" : "No"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.allow_recording")}
                          </Typography>
                          <Typography>
                            {data.allow_recording ? "Si" : "No"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.allow_transcription")}
                          </Typography>
                          <Typography>
                            {data.allow_transcription ? "Si" : "No"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.record_automatically")}
                          </Typography>
                          <Typography>
                            {data.record_automatically ? "Si" : "No"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.allowed_presenters")}
                          </Typography>
                          <Typography>
                            {data.allowed_presenters
                              ? inputTranslations(
                                  `options.role_for_presenter.${data.allowed_presenters.toLowerCase()}`,
                                )
                              : "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
                {data.tool && data.tool == "GMEET" && (
                  <>
                    <Box
                      mt={1}
                      sx={{
                        border: `1px solid ${theme.palette.text.tertiary}`,
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 1,
                        position: "relative",
                        gridColumn: "1 / -1",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          position: "absolute",
                          top: 0,
                          left: 10,
                          backgroundColor: theme.palette.background.paper,
                          transform: "translateY(-50%)",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("session-form.meeting-roles")}
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          rowGap: "1rem",
                          [theme.breakpoints.down("sm")]: {
                            gridTemplateColumns: "1fr",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.cohost")}
                          </Typography>
                          <Typography>{getArrayString(data.cohost)}</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      mt={1}
                      sx={{
                        border: `1px solid ${theme.palette.text.tertiary}`,
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 1,
                        position: "relative",
                        gridColumn: "1 / -1",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          position: "absolute",
                          top: 0,
                          left: 10,
                          backgroundColor: theme.palette.background.paper,
                          transform: "translateY(-50%)",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("session-form.meeting-lobby")}
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          rowGap: "1rem",
                          [theme.breakpoints.down("sm")]: {
                            gridTemplateColumns: "1fr",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.access_type")}
                          </Typography>
                          <Typography>
                            {data.access_type
                              ? inputTranslations(
                                  `options.meeting_access_type.${data.access_type.toLowerCase()}`,
                                )
                              : "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      mt={1}
                      sx={{
                        border: `1px solid ${theme.palette.text.tertiary}`,
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 1,
                        position: "relative",
                        gridColumn: "1 / -1",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          position: "absolute",
                          top: 0,
                          left: 10,
                          backgroundColor: theme.palette.background.paper,
                          transform: "translateY(-50%)",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("session-form.meeting-general")}
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          rowGap: "1rem",
                          [theme.breakpoints.down("sm")]: {
                            gridTemplateColumns: "1fr",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.chat_restriction")}
                          </Typography>
                          <Typography>
                            {data.chat_restriction
                              ? inputTranslations(
                                  `options.meeting_chat_restriction.${data.chat_restriction.toLowerCase()}`,
                                )
                              : "-"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.default_join_as_viewer_type")}
                          </Typography>
                          <Typography>
                            {data.default_join_as_viewer_type ? "Si" : "No"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.auto_transcription_generation")}
                          </Typography>
                          <Typography>
                            {data.auto_transcription_generation ? "Si" : "No"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.auto_recording_generation")}
                          </Typography>
                          <Typography>
                            {data.auto_recording_generation ? "Si" : "No"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
                {data.tool && data.tool == "OTHER" && (
                  <Box
                    mt={1}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      gridColumn: "1 / -1",
                      p: 0,
                    }}
                  >
                    <Box
                      sx={{
                        borderBottom: "1px solid white",
                        p: "1rem",
                      }}
                    >
                      <Typography fontWeight={600} mb={1}>
                        {t("session-form.link")}
                      </Typography>
                      <Typography
                        sx={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {data?.link || "-"}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {data.tool &&
                  data.online_meeting_updated_at &&
                  (data.tool == "GMEET" || data.tool == "MSTEAMS") && (
                    <Box
                      mt={1}
                      sx={{
                        border: `1px solid ${theme.palette.text.tertiary}`,
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 1,
                        position: "relative",
                        gridColumn: "1 / -1",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          position: "absolute",
                          top: 0,
                          left: 10,
                          backgroundColor: theme.palette.background.paper,
                          transform: "translateY(-50%)",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("session-form.link")}
                      </Box>
                      <Box
                        mt={1}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          gridColumn: "1 / -1",
                          p: 0,
                        }}
                      >
                        <Box
                          sx={{
                            borderBottom: "1px solid white",
                            p: "1rem",
                          }}
                        >
                          <Typography fontWeight={600} mb={1}>
                            {t("session-form.link")}
                          </Typography>
                          <Typography
                            sx={{
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {data?.url || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
              </>
            )}
            {!isVirtual && (
              <Box
                sx={{
                  borderBottom: "1px solid white",
                  p: "1rem",
                }}
              >
                <Typography fontWeight={600} mb={1}>
                  {t("session-form.qr-code")}
                </Typography>
                <Typography>
                  {data["QR-code"] && isTrueValue(data["QR-code"].toString())
                    ? "Si"
                    : "No"}
                </Typography>
              </Box>
            )}
          </Box>
          {data["QR-code"] && isDetail && (
            <Box
              sx={{
                mt: "2rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "20px",
                [theme.breakpoints.down("sm")]: {
                  flexDirection: "column",
                },
              }}
            >
              <ButtonCst
                id={"save-button"}
                variant={"contained"}
                size={"medium"}
                sx={{
                  minWidth: "150px",
                  [theme.breakpoints.down("lg")]: {
                    minWidth: "100px",
                  },
                  [theme.breakpoints.down("sm")]: {
                    minWidth: "100%",
                  },
                }}
                onClick={() => {
                  setShowQRModal(true);
                  setQRType("start");
                }}
              >
                {t("session-form.create-qr-code-start")}
              </ButtonCst>
              <ButtonCst
                id={"save-button"}
                variant={"contained"}
                size={"medium"}
                sx={{
                  minWidth: "150px",
                  [theme.breakpoints.down("lg")]: {
                    minWidth: "100px",
                  },
                  [theme.breakpoints.down("sm")]: {
                    minWidth: "100%",
                  },
                }}
                onClick={() => {
                  setShowQRModal(true);
                  setQRType("end");
                }}
              >
                {t("session-form.create-qr-code-end")}
              </ButtonCst>
            </Box>
          )}
          {!isDetail && (
            <>
              <Box
                sx={{
                  mt: "2rem",
                  mr: "1rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "20px",
                  [theme.breakpoints.down("sm")]: {
                    flexDirection: "column",
                  },
                }}
              >
                <IconButton
                  aria-label="delete session"
                  onClick={() => onDelete && onDelete(data)}
                  edge="end"
                  disabled={
                    disabled ||
                    (dayjs().isAfter(dayjs(data.publish_date_session)) &&
                      !isAddPage)
                  }
                >
                  <Delete />
                </IconButton>
                <IconButton
                  aria-label="edit session"
                  onClick={() => {
                    onEdit && onEdit(data, index);
                  }}
                  edge="end"
                  disabled={disabled}
                >
                  <Edit />
                </IconButton>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <ModalCst
        open={showQRModal}
        setOpen={setShowQRModal}
        typeOfModal={"sessionQR"}
        data={{ ...data, qr_type: QRType }}
        title={
          QRType === "start"
            ? t("session-form.create-qr-code-start") || ""
            : t("session-form.create-qr-code-end") || ""
        }
      />
      <SessionInvitationModalCst
        idMeeting={idMeeting || null}
        open={openModal}
        setOpen={setOpenModal}
        meeting={typeMeeting || null}
        role={roleView}
        onResendClick={handleResendClick}
      />
      <ModalConfirmsCst
        open={confirmResendNotifications}
        title={t("resend-notifications-title", { ns: "modal" })}
        description={t("confirm-resend-notifications", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setConfirmResendNotifications(false);
          setResendStatus(undefined);
        }}
        onActionConfirmed={() => {
          setConfirmResendNotifications(false);
          executeResendNotifications();
        }}
        status={resendStatus}
      />
      <ModalConfirmsCst
        open={!!openToast}
        title={t("outcome-item", { ns: "modal" })}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </>
  );
};

export default SessionAccordionCst;
